<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { Action } from '@seede/renderer/action';
import { cn } from '@/lib/utils';

const props = defineProps({
  class: String,
  contentClass: String,
  text: String,
  shortcut: {
    type: String,
    default: '',
  },
});

const tooltipRef = ref(null);
const triggerRef = ref(null);
const isVisible = ref(false);
const position = ref({ x: 0, y: 0 });
const placement = ref('top'); // 记录tooltip的位置
const arrowOffset = ref(0); // 箭头相对tooltip左侧的位置（px）
let showTimer = null;
let hideTimer = null;

const slots = defineSlots();

const tooltipStyle = computed(() => {
  const baseStyle = {
    position: 'fixed',
    left: `${position.value.x}px`,
    top: `${position.value.y}px`,
    zIndex: 10000, // 提高z-index确保在Select内容之上
  };

  // 根据位置调整transform
  if (placement.value === 'top') {
    return {
      ...baseStyle,
      transform: 'translate(-50%, -100%)',
      marginTop: '-8px',
    };
  } else {
    return {
      ...baseStyle,
      transform: 'translate(-50%, 0%)',
      marginTop: '8px',
    };
  }
});

const showTooltip = () => {
  clearTimeout(hideTimer);
  showTimer = setTimeout(() => {
    updatePosition();
    isVisible.value = true;
  }, 200);
};

const hideTooltip = () => {
  clearTimeout(showTimer);
  hideTimer = setTimeout(() => {
    isVisible.value = false;
  }, 100);
};

// 检查元素是否在可滚动容器内
const getScrollParent = (element) => {
  if (!element) return null;

  const overflowRegex = /(auto|scroll)/;
  let parent = element.parentElement;

  while (parent) {
    const style = window.getComputedStyle(parent);
    if (overflowRegex.test(style.overflow + style.overflowY + style.overflowX)) {
      return parent;
    }
    parent = parent.parentElement;
  }

  return document.documentElement;
};

const updatePosition = () => {
  if (!triggerRef.value) return;

  const rect = triggerRef.value.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  // 检查是否在可滚动容器内
  const scrollParent = getScrollParent(triggerRef.value);
  let containerRect = {
    top: 0,
    left: 0,
    right: viewportWidth,
    bottom: viewportHeight,
  };

  if (scrollParent && scrollParent !== document.documentElement) {
    containerRect = scrollParent.getBoundingClientRect();
  }

  // 优先使用已渲染的实际尺寸，否则使用估算尺寸
  const renderedRect = tooltipRef.value?.getBoundingClientRect?.();
  const tooltipWidth = renderedRect?.width ?? Math.min((props.text?.length || 0) * 8 + 32, 320);
  const tooltipHeight = renderedRect?.height ?? 40;

  let x = rect.left + rect.width / 2;
  let y = rect.top;
  const padding = 16;

  // 水平边缘检测和调整 - 考虑容器边界
  const leftBoundary = Math.max(containerRect.left + padding, padding);
  const rightBoundary = Math.min(containerRect.right - padding, viewportWidth - padding);

  if (x - tooltipWidth / 2 < leftBoundary) {
    x = leftBoundary + tooltipWidth / 2;
  }
  if (x + tooltipWidth / 2 > rightBoundary) {
    x = rightBoundary - tooltipWidth / 2;
  }

  // 垂直边缘检测和调整 - 考虑容器边界
  const topBoundary = Math.max(containerRect.top, 0);
  const bottomBoundary = Math.min(containerRect.bottom, viewportHeight);

  const spaceAbove = rect.top - topBoundary;
  const spaceBelow = bottomBoundary - rect.bottom;
  const fullTooltipHeight = tooltipHeight + 16; // 包含箭头和边距

  // 检查trigger是否在可见区域内
  const triggerVisible =
    rect.bottom > topBoundary &&
    rect.top < bottomBoundary &&
    rect.right > containerRect.left &&
    rect.left < containerRect.right;

  if (!triggerVisible) {
    isVisible.value = false;
    return;
  }

  // 智能选择位置：优先上方，但确保有足够空间
  const topHasEnoughSpace = spaceAbove >= fullTooltipHeight;
  const bottomHasEnoughSpace = spaceBelow >= fullTooltipHeight;

  if (topHasEnoughSpace && !bottomHasEnoughSpace) {
    // 只有上方有足够空间
    placement.value = 'top';
    y = rect.top;
  } else if (!topHasEnoughSpace && bottomHasEnoughSpace) {
    // 只有下方有足够空间
    placement.value = 'bottom';
    y = rect.bottom;
  } else if (topHasEnoughSpace && bottomHasEnoughSpace) {
    // 两边都有足够空间，优先使用上方
    placement.value = 'top';
    y = rect.top;
  } else {
    // 两边都没有足够空间，选择空间更大的那边
    if (spaceAbove > spaceBelow) {
      placement.value = 'top';
      y = rect.top;
    } else {
      placement.value = 'bottom';
      y = rect.bottom;
    }
  }

  position.value = { x, y };

  // 计算箭头在tooltip内部的偏移，使其指向trigger中心
  const triggerCenterX = rect.left + rect.width / 2;
  const tooltipLeft = x - tooltipWidth / 2;
  let desiredArrow = triggerCenterX - tooltipLeft; // 以tooltip左侧为基准
  const arrowMin = 8;
  const arrowMax = Math.max(arrowMin, tooltipWidth - 8);
  if (Number.isFinite(desiredArrow)) {
    desiredArrow = Math.min(arrowMax, Math.max(arrowMin, desiredArrow));
    arrowOffset.value = desiredArrow;
  }
};

const handleMouseEnter = (event) => {
  showTooltip(event);
};

const handleMouseLeave = () => {
  hideTooltip();
};

onMounted(() => {
  if (triggerRef.value) {
    triggerRef.value.addEventListener('mouseenter', handleMouseEnter);
    triggerRef.value.addEventListener('mouseleave', handleMouseLeave);
  }

  // 视口变化时更新位置
  const handleWindowChange = () => {
    if (isVisible.value) updatePosition();
  };
  window.addEventListener('resize', handleWindowChange);
  // 捕获阶段监听滚动，保证任意滚动容器变化都能捕捉
  window.addEventListener('scroll', handleWindowChange, true);

  // 记录销毁回调
  onUnmounted(() => {
    window.removeEventListener('resize', handleWindowChange);
    window.removeEventListener('scroll', handleWindowChange, true);
  });
});

onUnmounted(() => {
  clearTimeout(showTimer);
  clearTimeout(hideTimer);
  if (triggerRef.value) {
    triggerRef.value.removeEventListener('mouseenter', handleMouseEnter);
    triggerRef.value.removeEventListener('mouseleave', handleMouseLeave);
  }
});

// 当显示后，用下一帧的实际尺寸重新计算，避免溢出
watch(isVisible, async (val) => {
  if (val) {
    await nextTick();
    updatePosition();
  }
});

// 文本或快捷键变化时，重新计算位置与箭头
watch(
  () => [props.text, props.shortcut],
  async () => {
    if (isVisible.value) {
      await nextTick();
      updatePosition();
    }
  }
);
</script>

<template>
  <div ref="triggerRef" :class="props.class">
    <slot />
    <Teleport to="body">
      <Transition
        enter-active-class="transition-all duration-150 ease-out"
        leave-active-class="transition-all duration-100 ease-in"
        enter-from-class="opacity-0 scale-95 translate-y-1"
        enter-to-class="opacity-100 scale-100 translate-y-0"
        leave-from-class="opacity-100 scale-100 translate-y-0"
        leave-to-class="opacity-0 scale-95 translate-y-1"
      >
        <div
          v-if="isVisible && (text || slots.content)"
          ref="tooltipRef"
          :style="tooltipStyle"
          :class="
            cn(
              'px-3 py-2 bg-black/90 text-white text-xs rounded-lg shadow-xl pointer-events-none select-none max-w-xs relative',
              props.contentClass
            )
          "
        >
          <!-- 如果有slot content，使用slot，否则使用text -->
          <div v-if="slots.content" class="leading-relaxed">
            <slot name="content"></slot>
          </div>

          <div v-else class="flex gap-2 items-center">
            <span class="leading-relaxed break-words">{{ text }}</span>
            <span v-if="shortcut" class="text-xs text-black/20 opacity-75 whitespace-nowrap">
              {{ Action.command(shortcut) }}
            </span>
          </div>

          <!-- Tooltip arrow - 根据位置调整，使用百分比定位 -->
          <div
            v-if="placement === 'top'"
            class="absolute top-full"
            :style="{
              left: arrowOffset + 'px',
              transform: 'translateX(-50%)',
            }"
          >
            <div
              class="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black/90"
            ></div>
          </div>

          <div
            v-else
            class="absolute bottom-full"
            :style="{
              left: arrowOffset + 'px',
              transform: 'translateX(-50%)',
            }"
          >
            <div
              class="w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-black/90"
            ></div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>
